/**
 * Filling Activity Attribute Rules
 *
 * Defines attribute behavior for filling activities across all statuses
 * Now using shared attribute library for DRY code
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig } from "../types"
import {
  pressureForStarted,
  pressureForCompleted,
  pressureHidden,
  temperatureForStarted,
  temperatureForCompleted,
  temperatureHidden,
  fillLevelForStarted,
  siteForScheduled,
  siteInherited,
  baseNoteConfig,
  getDefaultAsset
} from "../shared"
import { findAttributeValue } from "../shared/helpers"

/**
 * Asset configuration for Scheduled status
 * Editable during creation, read-only when editing existing activity
 */
const assetConfigForScheduled: AttributeConfig = {
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  // Only editable when creating a new activity (temp ID), not when editing existing
  editable: (ctx) => ctx.activity.id.startsWith("temp"),
  required: false,
  helpText: "Select an asset for this activity",
  order: 1, // First field
  defaultValue: (ctx) => {
    // Default to first order asset if none selected
    return getDefaultAsset(ctx)
  },
}

/**
 * Asset configuration for non-scheduled statuses (Started/Completed)
 * Inherited chronologically from previous status, read-only
 */
const assetConfigInherited = (fromEventKind: gqlTypes.ActivityEventKind): AttributeConfig => ({
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  editable: false,
  required: false,
  helpText: "Asset for this activity (use Edit Asset button to change)",
  order: 1,
  defaultValue: (ctx) => {
    // Inherit asset from the specified previous status
    const assetValue = findAttributeValue(ctx, "asset", [fromEventKind])
    if (assetValue) return assetValue

    // Fallback to first order asset if not found in previous status
    return getDefaultAsset(ctx)
  },
})

/**
 * Tank level for Completed - calculated only (NOT IoT)
 * Calculator is now in baseFillLevelConfig (shared across all activities)
 */
const fillLevelForCompletedCalculated: AttributeConfig = {
  ...fillLevelForStarted(),
  visibility: "calculated",
  helpText: "Final tank level (auto-calculated from pressure × temperature × volume)",
  // Calculator already included from baseFillLevelConfig
  // NO IoT source - calculated fields only
}

/**
 * Attribute rules for Filling activities
 * Organized by status: Scheduled → Started → Completed
 */
export const MAINTENANCE_RULES: Record<gqlTypes.ActivityEventKind, AttributeConfig[]> = {
  [gqlTypes.ActivityEventKind.Pending]: [],

  /**
   * SCHEDULED STATUS
   * Planning phase - set quantity, optionally site and batch
   */
  [gqlTypes.ActivityEventKind.Scheduled]: [
    assetConfigForScheduled,
    { ...siteForScheduled(""), order: 3 },
    { ...pressureHidden, order: 5 },
    { ...temperatureHidden, order: 6 },
    { ...baseNoteConfig, order: 7 },
  ],

  /**
   * STARTED STATUS
   * Filling in progress - site/batch inherited (editable if not set), record current readings
   */
  [gqlTypes.ActivityEventKind.Started]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Scheduled),
    { ...siteInherited("", gqlTypes.ActivityEventKind.Scheduled, true), order: 2 }, // Editable if not inherited
    { ...pressureForStarted(), order: 4 }, // Pressure and temperature on same row, IoT-enabled
    { ...temperatureForStarted(), order: 5 }, // IoT-enabled
    { ...fillLevelForStarted(), order: 6 }, // Tank level right after P/T
    { ...baseNoteConfig, order: 7 },
  ],

  /**
   * COMPLETED STATUS
   * Filling complete - record final readings, calculate quantity
   */
  [gqlTypes.ActivityEventKind.Completed]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Started),
    {
      ...siteInherited("", gqlTypes.ActivityEventKind.Started),
      order: 2,
      // Fallback to Scheduled if not found in Started
      defaultValue: (ctx) => {
        const startedSite = ctx.activity.attributes?.find(
          (attr) =>
            attr.attribute === gqlTypes.ActivityAttributeKind.Site &&
            attr.event_kind === gqlTypes.ActivityEventKind.Started
        )
        if (startedSite?.site?.id) return startedSite.site.id

        const scheduledSite = ctx.activity.attributes?.find(
          (attr) =>
            attr.attribute === gqlTypes.ActivityAttributeKind.Site &&
            attr.event_kind === gqlTypes.ActivityEventKind.Scheduled
        )
        return scheduledSite?.site?.id || null
      },
    },
    // IoT-enabled sensor readings - P/T on same row
    { ...pressureForCompleted(), order: 4 }, // IoT-enabled
    { ...temperatureForCompleted(), order: 5 }, // IoT-enabled
    { ...fillLevelForCompletedCalculated, order: 6 }, // Tank level right after P/T
    { ...baseNoteConfig, order: 7 },
  ],
}
